/**
 * Bills List Page
 * BuilderBel Construction Project Management System
 */
import {
  Check,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Eye,
  FileText,
  Inbox,
  Pencil,
  Plus,
  Receipt,
  Trash2,
  XCircle,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { SearchableDropdown } from '../components/forms';
import LoadingSpinner from '../components/LoadingSpinner';
import { BillDrawer } from '../components/bills';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useDeleteBillMutation,
  useGetBillsQuery,
  useGetBillStatsQuery,
  useProcessBillApprovalMutation,
  useSubmitBillForApprovalMutation,
  useMarkBillAsPaidMutation,
} from '../services/billApi';
import type { Bill, BillStatus } from '../types/bill';

// Status filter options - matches backend enum
const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'void', label: 'Void' },
];

// Sort options - matches backend listQuerySchema
const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Created Date' },
  { value: 'billDate', label: 'Bill Date' },
  { value: 'dueDate', label: 'Due Date' },
  { value: 'total', label: 'Amount' },
  { value: 'billNumber', label: 'Bill Number' },
  { value: 'status', label: 'Status' },
];

// Get status variant for StatusBadge - matches backend status enum
const getStatusVariant = (
  status: BillStatus
): 'success' | 'warning' | 'error' | 'info' | 'purple' | 'neutral' => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'approved':
      return 'info';
    case 'submitted':
    case 'partial':
      return 'warning';
    case 'cancelled':
    case 'void':
      return 'error';
    case 'draft':
    default:
      return 'neutral';
  }
};

// Format currency
const formatCurrency = (amount: number, currency = 'USD'): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Format date
const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const Bills: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // State for filters and pagination
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<BillStatus | ''>('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const limit = 20;

  // State for drawer
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'view'>('create');

  // State for dropdown menu
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  // API calls
  const {
    data: billsResponse,
    isLoading,
    isFetching,
    refetch,
  } = useGetBillsQuery(
    {
      projectId: projectId || '',
      params: {
        page,
        limit,
        search: search || undefined,
        status: status || undefined,
        sortBy: sortBy as 'createdAt' | 'billDate' | 'dueDate' | 'total' | 'billNumber' | 'status',
        sortOrder,
      },
    },
    { skip: !projectId }
  );

  const { data: statsResponse } = useGetBillStatsQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  const [deleteBill, { isLoading: isDeleting }] = useDeleteBillMutation();
  const [submitForApproval] = useSubmitBillForApprovalMutation();
  const [processApproval] = useProcessBillApprovalMutation();
  const [markAsPaid] = useMarkBillAsPaidMutation();

  // Extract data
  const bills = useMemo(() => billsResponse?.data?.bills || [], [billsResponse]);
  const pagination = billsResponse?.data?.pagination;
  const stats = statsResponse?.data;

  // Computed stats for header
  const headerStats = useMemo(() => {
    if (!stats) return [];
    return [
      {
        title: 'Total',
        value: stats.overview?.totalBills || 0,
        variant: 'brand' as const,
      },
      {
        title: 'Pending',
        value: (stats.byStatus?.submitted?.count || 0) + (stats.byStatus?.draft?.count || 0),
        variant: 'warning' as const,
      },
      {
        title: 'Paid',
        value: stats.byStatus?.paid?.count || 0,
        variant: 'success' as const,
      },
      {
        title: 'Overdue',
        value: stats.overdue?.count || 0,
        variant: 'danger' as const,
      },
    ];
  }, [stats]);

  // Handlers
  const handleCreateBill = useCallback(() => {
    setSelectedBill(null);
    setDrawerMode('create');
    setIsDrawerOpen(true);
  }, []);

  const handleViewBill = useCallback((bill: Bill) => {
    setSelectedBill(bill);
    setDrawerMode('view');
    setIsDrawerOpen(true);
  }, []);

  const handleEditBill = useCallback((bill: Bill) => {
    setSelectedBill(bill);
    setDrawerMode('edit');
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedBill(null);
  }, []);

  const handleDeleteBill = useCallback(
    async (bill: Bill) => {
      if (!projectId) return;
      if (window.confirm(`Are you sure you want to delete Bill ${bill.billNumber}?`)) {
        try {
          await deleteBill({
            projectId,
            billId: bill._id,
          }).unwrap();
          toast.success('Bill deleted successfully');
        } catch {
          toast.error('Failed to delete bill');
        }
      }
    },
    [projectId, deleteBill, toast]
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  // Handle Submit for Approval
  const handleSubmitForApproval = useCallback(
    async (bill: Bill) => {
      if (!projectId) return;
      try {
        await submitForApproval({ projectId, billId: bill._id }).unwrap();
        toast.success('Bill submitted for approval');
        refetch();
      } catch {
        toast.error('Failed to submit bill for approval');
      }
    },
    [projectId, submitForApproval, toast, refetch]
  );

  // Handle Approve
  const handleApprove = useCallback(
    async (bill: Bill) => {
      if (!projectId) return;
      try {
        await processApproval({
          projectId,
          billId: bill._id,
          data: { approved: true },
        }).unwrap();
        toast.success('Bill approved');
        refetch();
      } catch {
        toast.error('Failed to approve bill');
      }
    },
    [projectId, processApproval, toast, refetch]
  );

  // Handle Reject
  const handleReject = useCallback(
    async (bill: Bill) => {
      if (!projectId) return;
      const reason = window.prompt('Please enter rejection reason:');
      if (!reason) return;
      try {
        await processApproval({
          projectId,
          billId: bill._id,
          data: { approved: false, comments: reason },
        }).unwrap();
        toast.success('Bill rejected');
        refetch();
      } catch {
        toast.error('Failed to reject bill');
      }
    },
    [projectId, processApproval, toast, refetch]
  );

  // Handle Mark as Paid
  const handleMarkAsPaid = useCallback(
    async (bill: Bill) => {
      if (!projectId) return;
      try {
        await markAsPaid({
          projectId,
          billId: bill._id,
          data: {
            paymentDate: new Date().toISOString().split('T')[0],
            paymentMethod: 'check',
          },
        }).unwrap();
        toast.success('Bill marked as paid');
        refetch();
      } catch {
        toast.error('Failed to mark bill as paid');
      }
    },
    [projectId, markAsPaid, toast, refetch]
  );

  // Render filters
  const renderFilters = () => (
    <div className="flex items-center gap-2">
      <SearchableDropdown
        placeholder="Status"
        value={status}
        onChange={value => {
          setStatus(value as BillStatus | '');
          setPage(1);
        }}
        options={STATUS_OPTIONS}
        searchable={false}
      />
    </div>
  );

  // Render pagination
  const renderPagination = () => {
    if (!pagination || pagination.totalPages <= 1) return null;

    const { page: currentPage, totalPages, total } = pagination;
    const startItem = (currentPage - 1) * limit + 1;
    const endItem = Math.min(currentPage * limit, total);

    return (
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          Showing {startItem}-{endItem} of {total} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  // Render row actions dropdown using portal-based ActionMenu
  const renderRowActions = (bill: Bill) => {
    const isMenuOpen = activeMenuId === bill._id;

    const menuItems: (ActionMenuItem | 'divider')[] = [
      // View & Edit
      {
        label: 'View Details',
        icon: <Eye className="h-3.5 w-3.5" />,
        onClick: () => handleViewBill(bill),
      },
      {
        label: 'Edit',
        icon: <Pencil className="h-3.5 w-3.5" />,
        onClick: () => handleEditBill(bill),
        hidden: bill.status !== 'draft',
      },
      'divider',
      // Status Actions
      {
        label: 'Submit for Approval',
        icon: <FileText className="h-3.5 w-3.5" />,
        onClick: () => handleSubmitForApproval(bill),
        hidden: bill.status !== 'draft',
      },
      {
        label: 'Approve',
        icon: <Check className="h-3.5 w-3.5" />,
        onClick: () => handleApprove(bill),
        variant: 'success',
        hidden: bill.status !== 'submitted',
      },
      {
        label: 'Reject',
        icon: <XCircle className="h-3.5 w-3.5" />,
        onClick: () => handleReject(bill),
        variant: 'warning',
        hidden: bill.status !== 'submitted',
      },
      'divider',
      // Payment Actions
      {
        label: 'Mark as Paid',
        icon: <CreditCard className="h-3.5 w-3.5" />,
        onClick: () => handleMarkAsPaid(bill),
        variant: 'success',
        hidden: bill.status !== 'approved' && bill.status !== 'partial',
      },
      'divider',
      // Delete
      {
        label: 'Delete',
        icon: <Trash2 className="h-3.5 w-3.5" />,
        onClick: () => handleDeleteBill(bill),
        variant: 'danger',
        disabled: isDeleting,
        hidden: bill.status !== 'draft' && bill.status !== 'cancelled',
      },
    ];

    return (
      <ActionMenu
        id={bill._id}
        items={menuItems}
        isOpen={isMenuOpen}
        onOpenChange={open => setActiveMenuId(open ? bill._id : null)}
      />
    );
  };

  // Show loading state
  if (isLoading && bills.length === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  // Show project selection required
  if (!projectId) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <EmptyState
          icon={Inbox}
          title="No Project Selected"
          description="Please select a project to view bills."
        />
      </div>
    );
  }

  return (
    <ModuleLayout
      title="Bills"
      description="Track and manage vendor bills and payments"
      icon={<Receipt className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'New Bill',
        onClick: handleCreateBill,
        icon: <Plus className="h-3 w-3 mr-1" />,
      }}
      stats={headerStats}
      searchValue={search}
      onSearchChange={value => {
        setSearch(value);
        setPage(1);
      }}
      filters={renderFilters()}
      sortOptions={SORT_OPTIONS}
      sortValue={sortBy}
      onSortChange={setSortBy}
      sortOrder={sortOrder}
      onSortOrderChange={setSortOrder}
      onRefresh={refetch}
      isLoading={isFetching}
      pagination={renderPagination()}
    >
      {bills.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="No bills found"
          description={
            search || status
              ? "Try adjusting your filters to find what you're looking for."
              : 'Get started by creating your first bill.'
          }
          action={
            !search && !status
              ? {
                  label: 'Create Bill',
                  onClick: handleCreateBill,
                }
              : undefined
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bill Number
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bill Date
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {bills.map(bill => (
                <tr
                  key={bill._id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleViewBill(bill)}
                >
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900">{bill.billNumber}</span>
                      {bill.title && (
                        <span className="text-xs text-gray-500 truncate max-w-[200px]">
                          {bill.title}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-900">{bill.vendor.name}</span>
                      {bill.vendor.contactName && (
                        <span className="text-xs text-gray-500">{bill.vendor.contactName}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(bill.total, bill.currency)}
                      </span>
                      {bill.paidAmount > 0 && bill.paidAmount < bill.total && (
                        <span className="text-xs text-orange-600">
                          Due: {formatCurrency(bill.remainingBalance || 0, bill.currency)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge
                      status={bill.status}
                      variant={getStatusVariant(bill.status)}
                      size="sm"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{formatDate(bill.billDate)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-sm ${
                        bill.isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'
                      }`}
                    >
                      {formatDate(bill.dueDate)}
                    </span>
                  </td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    {renderRowActions(bill)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Bill Drawer */}
      <BillDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        bill={selectedBill}
        mode={drawerMode}
        onSuccess={refetch}
      />
    </ModuleLayout>
  );
};

export default Bills;
